import React, { useState } from 'react';
import axios from 'axios';
import { History, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  accepted: { label: 'Accepted', cls: 'bg-success/15 text-success border-success/30' },
  approved: { label: 'Approved', cls: 'bg-success/15 text-success border-success/30' },
  rescheduled: { label: 'Rebooked', cls: 'bg-success/15 text-success border-success/30' },
  declined: { label: 'Declined', cls: 'bg-alert/10 text-alert border-alert/30' },
  reschedule_offered: { label: 'Offer Sent', cls: 'bg-amber-400/10 text-amber-400 border-amber-400/20' },
};

export function RequestHistory() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && items === null) {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/request-history`, { withCredentials: true });
        setItems(data);
      } catch (e) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div data-testid="request-history">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between bg-surface border border-border hover:border-primary/50 rounded-xl p-4 transition-colors"
        data-testid="request-history-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-foreground">
          <History className="w-4 h-4 text-primary" />
          Request History
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-text-muted" />
        ) : (
          <ChevronDown className="w-4 h-4 text-text-muted" />
        )}
      </button>

      {open && (
        <div className="mt-3 space-y-2" data-testid="request-history-list">
          {loading ? (
            <p className="text-sm text-text-secondary p-2">Loading...</p>
          ) : items && items.length === 0 ? (
            <p className="text-sm text-text-secondary bg-surface border border-border rounded-xl p-4 text-center">
              No decisions yet — accepted, declined and rescheduled requests will appear here.
            </p>
          ) : (
            (items || []).map((item, i) => {
              const meta = STATUS_META[item.status] || { label: item.status, cls: 'bg-surface-raised text-text-secondary border-border' };
              return (
                <div key={item.id || item.request_id || `${item.client_name}-${item.decided_at}-${i}`} className="bg-surface border border-border rounded-xl p-3" data-testid={`history-item-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground text-sm">{item.client_name}</span>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${meta.cls}`}>
                          {meta.label}
                        </span>
                        <span className="text-[10px] text-text-muted uppercase tracking-wide">
                          {item.kind === 'booking' ? 'Booking' : 'Date change'}
                        </span>
                      </div>
                      <div className="text-xs text-text-secondary mt-1 flex items-center gap-1.5 flex-wrap">
                        <span>{item.detail}</span>
                        {item.to_date && (
                          <>
                            <ArrowRight className="w-3 h-3 text-primary" />
                            <span className="text-primary">{item.to_date}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {item.decided_at && (
                      <span className="text-[10px] text-text-muted flex-shrink-0">
                        {item.decided_at.split('T')[0]}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
